"use client";

import { useMemo, useState } from "react";
import type { ZodError } from "zod";
import {
  Button,
  Checkbox,
  Input,
  Select,
  Textarea,
} from "@wmart/ui";
import {
  CONTACT_ENQUIRY_OPTIONS,
  CONTACT_ORDER_NUMBER_REQUIRED_TYPES,
  ContactSubmissionFieldsSchema,
  type ContactSubmissionResponseDTO,
} from "@wmart/shared-types";
import { ApiError, clientApi } from "@/lib/api-client";
import { CONTACT_PAGE_CONTENT } from "./contact-content";

interface ContactFormValues {
  fullName: string;
  businessName: string;
  email: string;
  phone: string;
  enquiryType: string;
  orderNumber: string;
  message: string;
  privacyAccepted: boolean;
  attachment: File | null;
}

const ORDER_NUMBER_REQUIRED = new Set(CONTACT_ORDER_NUMBER_REQUIRED_TYPES);
const ALLOWED_CLIENT_ATTACHMENT_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
]);
const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;

const INITIAL_VALUES: ContactFormValues = {
  fullName: "",
  businessName: "",
  email: "",
  phone: "",
  enquiryType: "",
  orderNumber: "",
  message: "",
  privacyAccepted: false,
  attachment: null,
};

function zodErrorsToFieldMap(err: ZodError): Record<string, string> {
  const map: Record<string, string> = {};
  for (const issue of err.issues) {
    const key = issue.path.join(".") || "_root";
    if (!(key in map)) {
      map[key] = issue.message;
    }
  }
  return map;
}

function validateAttachment(file: File | null): string | null {
  if (!file) return null;
  if (file.size > ATTACHMENT_MAX_BYTES) {
    return "Attachment exceeds the 5MB limit.";
  }
  if (file.type && !ALLOWED_CLIENT_ATTACHMENT_TYPES.has(file.type)) {
    return "Unsupported attachment type. Only PDF, JPG, and PNG are allowed.";
  }
  return null;
}

export function ContactPageClient() {
  const [values, setValues] = useState<ContactFormValues>(INITIAL_VALUES);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] =
    useState<ContactSubmissionResponseDTO | null>(null);

  const selectedOption = useMemo(
    () =>
      CONTACT_ENQUIRY_OPTIONS.find(
        (option) => option.value === values.enquiryType,
      ) ?? null,
    [values.enquiryType],
  );
  const showOrderNumber = ORDER_NUMBER_REQUIRED.has(
    values.enquiryType as (typeof CONTACT_ORDER_NUMBER_REQUIRED_TYPES)[number],
  );
  const privacyPolicyLink = CONTACT_PAGE_CONTENT.helpfulLinks.find(
    (link) => link.label === "Privacy Policy",
  );

  const updateField =
    (key: keyof ContactFormValues) =>
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) => {
      const nextValue =
        event.target instanceof HTMLInputElement && event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value;
      setValues((current) => ({ ...current, [key]: nextValue }));
      setFieldErrors((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
    };

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setBanner(null);
    setFieldErrors({});

    const attachmentError = validateAttachment(values.attachment);
    if (attachmentError) {
      setBusy(false);
      setFieldErrors({ attachment: attachmentError });
      return;
    }

    const parsed = ContactSubmissionFieldsSchema.safeParse({
      fullName: values.fullName,
      businessName: values.businessName,
      email: values.email,
      phone: values.phone,
      enquiryType: values.enquiryType,
      orderNumber: values.orderNumber,
      message: values.message,
      privacyAccepted: values.privacyAccepted,
    });

    if (!parsed.success) {
      setBusy(false);
      setFieldErrors(zodErrorsToFieldMap(parsed.error));
      return;
    }

    const formData = new FormData();
    formData.set("fullName", parsed.data.fullName);
    formData.set("businessName", parsed.data.businessName);
    formData.set("email", parsed.data.email);
    formData.set("phone", parsed.data.phone ?? "");
    formData.set("enquiryType", parsed.data.enquiryType);
    formData.set("orderNumber", parsed.data.orderNumber ?? "");
    formData.set("message", parsed.data.message);
    formData.set("privacyAccepted", String(parsed.data.privacyAccepted));
    if (values.attachment) {
      formData.set("attachment", values.attachment);
    }

    try {
      const response = await clientApi.postForm<ContactSubmissionResponseDTO>(
        "/v1/storefront/contact",
        formData,
      );
      setSubmitted(response);
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.status === 422 &&
        (error.problem as unknown as { extensions?: { fields?: Record<string, string> } })
          ?.extensions?.fields
      ) {
        setFieldErrors(
          (
            error.problem as unknown as {
              extensions: { fields: Record<string, string> };
            }
          ).extensions.fields,
        );
      } else if (
        error instanceof ApiError &&
        (error.status === 413 || error.status === 415)
      ) {
        setFieldErrors({
          attachment:
            error.problem?.detail ??
            (error.status === 413
              ? "Attachment exceeds the 5MB limit."
              : "Unsupported attachment type."),
        });
      } else if (error instanceof ApiError) {
        setBanner(error.problem?.detail ?? error.problem?.title ?? error.message);
      } else {
        setBanner(error instanceof Error ? error.message : "Something went wrong.");
      }
    } finally {
      setBusy(false);
    }
  }

  if (submitted) {
    return (
      <div className="rounded-sm border border-accent/30 bg-accent-soft/40 p-6">
        <p className="text-overline font-semibold uppercase tracking-[0.06em] text-accent">
          Message received
        </p>
        <h3 className="mt-3 font-serif text-display-sm font-normal text-ink-900">
          Thank you for contacting WMart.
        </h3>
        <p className="mt-4 max-w-2xl text-body-lg leading-[1.7] text-ink-700">
          We have received your message and will respond as soon as possible.
          Please check your email for confirmation.
        </p>
        {submitted.responseTargetText ? (
          <p className="mt-4 text-body text-ink-700">
            Target response time: <strong>{submitted.responseTargetText}</strong>.
          </p>
        ) : null}
        {!submitted.acknowledgementEmailSent ? (
          <p className="mt-3 text-body-sm text-ink-500">
            Your enquiry was saved successfully even though the acknowledgement
            email could not be sent.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-6">
      {banner ? (
        <div className="rounded-sm border border-danger bg-danger-soft px-4 py-3 text-body-sm text-danger">
          {banner}
        </div>
      ) : null}

      <div className="grid gap-5 md:grid-cols-2">
        <Input
          name="fullName"
          label="Full name"
          required
          autoComplete="name"
          value={values.fullName}
          onChange={updateField("fullName")}
          error={fieldErrors.fullName}
        />
        <Input
          name="businessName"
          label="Business name"
          required
          autoComplete="organization"
          value={values.businessName}
          onChange={updateField("businessName")}
          error={fieldErrors.businessName}
        />
        <Input
          name="email"
          type="email"
          label="Email address"
          required
          autoComplete="email"
          value={values.email}
          onChange={updateField("email")}
          error={fieldErrors.email}
        />
        <Input
          name="phone"
          type="tel"
          label="Phone number"
          helper="Optional"
          autoComplete="tel"
          value={values.phone}
          onChange={updateField("phone")}
          error={fieldErrors.phone}
        />
      </div>

      <Select
        name="enquiryType"
        label="Subject / enquiry type"
        required
        value={values.enquiryType}
        onChange={updateField("enquiryType")}
        error={fieldErrors.enquiryType}
        helper={
          selectedOption?.responseTargetText
            ? `Target response time: ${selectedOption.responseTargetText}`
            : "Select the topic that best matches your enquiry."
        }
      >
        <option value="">Choose an enquiry type</option>
        {CONTACT_ENQUIRY_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>

      {showOrderNumber ? (
        <Input
          name="orderNumber"
          label="Order number"
          required
          helper="WMart needs your order number to investigate this enquiry efficiently."
          value={values.orderNumber}
          onChange={updateField("orderNumber")}
          error={fieldErrors.orderNumber}
        />
      ) : null}

      <Textarea
        name="message"
        label="Message"
        required
        minLength={20}
        rows={7}
        helper="Please include enough detail for the WMart team to help you."
        value={values.message}
        onChange={updateField("message")}
        error={fieldErrors.message}
      />

      <div className="flex flex-col gap-1.5">
        <label htmlFor="attachment" className="text-body-sm font-medium text-ink-700">
          Attach a file
        </label>
        <input
          id="attachment"
          name="attachment"
          type="file"
          accept=".pdf,image/png,image/jpeg"
          onChange={(event) => {
            const nextFile = event.target.files?.[0] ?? null;
            setValues((current) => ({ ...current, attachment: nextFile }));
            setFieldErrors((current) => {
              const next = { ...current };
              delete next.attachment;
              return next;
            });
          }}
          className="block w-full rounded-sm border border-ink-200 bg-white px-3 py-2 text-body text-ink-900 outline-none focus:border-accent focus:shadow-focus"
        />
        {!fieldErrors.attachment ? (
          <p className="text-body-sm text-ink-500">
            Optional supporting file. Accepted types: PDF, JPG, PNG. Maximum
            size: 5MB.
          </p>
        ) : null}
        {fieldErrors.attachment ? (
          <p className="text-body-sm text-danger" role="alert">
            {fieldErrors.attachment}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-2">
        <Checkbox
          name="privacyAccepted"
          checked={values.privacyAccepted}
          onChange={updateField("privacyAccepted")}
          label={
            <span>
              I agree to the{" "}
              {privacyPolicyLink ? (
                <a
                  href={privacyPolicyLink.url}
                  className="text-accent underline decoration-accent/40 underline-offset-4"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>
              ) : (
                "Privacy Policy"
              )}
              .
            </span>
          }
        />
        {fieldErrors.privacyAccepted ? (
          <p className="text-body-sm text-danger" role="alert">
            {fieldErrors.privacyAccepted}
          </p>
        ) : null}
      </div>

      <div>
        <Button type="submit" size="lg" loading={busy} disabled={busy}>
          {busy ? "Sending..." : "Send message"}
        </Button>
      </div>
    </form>
  );
}
